import { useState } from 'react';
import { Modal } from '../../../../components/Modal/Modal';
import { ModalParams } from '../../../../hooks/useModal';
import { GraniteButton } from '../../../../components/V2/Button/GraniteButton';
import Checkbox from 'components/Checkbox';
import { formatCurrency } from 'shared/util/formatCurrency';
import PriceBreakdown from './PriceBreakdown';
import { useForm } from 'react-hook-form';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  AccessReviewOrderForm,
  AccessReviewOrderFormSchema,
} from 'api/accessexpressv3/schemas/AccessCheckoutSchemas';
import Loader from 'components/Loader';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import {
  getNonRecurringCharges,
  getRecurringCharges,
  getServiceList,
} from 'screens/AccessExpressV3/utils';
import { useAccessCheckoutContext } from 'screens/AccessExpressV3/context/AccessCheckoutProvider';

export interface AccessReviewOrderModalProps extends ModalParams {
  onConfirmation: (
    formData: AccessReviewOrderForm & { invoiceId?: string },
  ) => void;
  isCheckoutLoading?: boolean;
}

export const AccessReviewOrderModal = ({
  isOpen,
  close,
  onConfirmation,
  isCheckoutLoading,
}: AccessReviewOrderModalProps) => {
  const cart = useCartContext();
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const { form } = useAccessCheckoutContext();
  const locations = form.getValues('locations') ?? [];

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<AccessReviewOrderForm>({
    resolver: zodResolver(AccessReviewOrderFormSchema),
  });

  const onSubmit = (data: AccessReviewOrderForm) => {
    const invoiceId = cart.items.find((item) => item.item_details?.invoice_id)
      ?.item_details?.invoice_id as string;

    if (onConfirmation) {
      onConfirmation({
        ...data,
        invoiceId,
      });
    }
  };

  const nonRecurringCharges = getNonRecurringCharges(cart.items);
  const recurringCharges = getRecurringCharges(cart.items);
  const services = getServiceList(cart.items);

  return (
    <Modal
      isVisible={isOpen}
      close={close}
      className="vertical-scrollbar z-50 max-h-[90vh] max-w-[624px] overflow-y-auto"
    >
      <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6">
        <h1 className="text-[28px] leading-9 text-content-base-default">
          Review & finalize order
        </h1>
        <p className="text-base font-semibold leading-[22px] text-content-base-subdued">
          Take a moment to review the details of your order. This includes your
          selected plans, any additional services, and the total cost.
        </p>
      </div>
      <div className="flex flex-col p-8">
        {/* {mobilityFormData.products.length > 0 && (
          <>
            <h1 className="text-base font-bold text-content-base-default">
              Your cart ({mobilityFormData.products?.length ?? 0})
            </h1>
            <div className="mb-[20px] flex  h-full flex-col gap-2 overflow-x-scroll rounded bg-[#28283B] p-2 scrollbar-none">
              <ProductTypeTag type={mobilityFormData.productType} />
              <div className="flex h-full gap-4">
                {mobilityFormData.products.map((product) => (
                  <div
                    key={product.id}
                    className="flex w-[90px] flex-col gap-2"
                  >
                    <div className="min-h-[64px]">
                      <CartItemImage item={product} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <h1
                        title={product.name}
                        className="max-h-4 w-[70px] overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold text-content-base-default"
                      >
                        {product.name}
                      </h1>
                      {!!product.item_details?.totalMRC && (
                        <ProductPriceInfo
                          price={Number(product.item_details?.totalMRC ?? 0)}
                          count={Number(product.quantity ?? 0)}
                          repeat={true}
                        />
                      )}
                      {!!product.item_details?.totalNRC && (
                        <ProductPriceInfo
                          price={Number(product.item_details?.totalNRC ?? 0)}
                          count={Number(product.quantity ?? 0)}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )} */}
        <PriceBreakdown
          info={locations.length}
          title="Total locations"
          className="rounded-t !p-4 !pb-0"
        >
          {!!locations.length && (
            <div className="flex flex-col gap-[10px]">
              {locations.map((location, index) => (
                <span
                  key={index}
                  className="text-base font-bold text-content-base-default"
                >
                  {index + 1}.&nbsp;
                  {[
                    location.address_line_1,
                    location.city,
                    location.state,
                    location.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              ))}
            </div>
          )}
        </PriceBreakdown>
        <PriceBreakdown
          info={services.length}
          title="Total services"
          className="!p-4 !pb-0"
        >
          {!!services.length && (
            <div className="flex flex-col gap-[10px]">
              {services.map((service, index) => (
                <span
                  key={index}
                  className="text-base font-bold text-content-base-default"
                >
                  {service}
                </span>
              ))}
            </div>
          )}
        </PriceBreakdown>
        <PriceBreakdown
          info={
            <span className="font-bold text-content-accent-default">
              {formatCurrency(nonRecurringCharges.grandTotal)}
            </span>
          }
          title="Total non-recurring charges"
          className="!p-4 !pb-0"
        >
          <div className="flex flex-col gap-[10px] text-base font-bold">
            {nonRecurringCharges.charges.map((item, index) => (
              <div key={index} className="flex justify-between gap-3">
                <div className="flex flex-row gap-1">
                  <span className="text-content-base-default">{item.name}</span>
                  {'quantity' in item && (
                    <div className="whitespace-nowrap text-content-base-subdued">
                      x {String(item.quantity)}
                    </div>
                  )}
                </div>
                <span className="text-content-base-subdued">
                  {formatCurrency(item.price)}
                </span>
              </div>
            ))}
          </div>
        </PriceBreakdown>
        <PriceBreakdown
          info={
            <span className="font-bold text-content-accent-default">
              {formatCurrency(recurringCharges.grandTotal)}
            </span>
          }
          title="Total recurring charges"
          className="rounded-b !p-4"
        >
          <div className="flex flex-col gap-[10px] text-base font-bold">
            {recurringCharges.charges.map((item, index) => (
              <div key={index} className="flex justify-between gap-3">
                <div className="flex flex-row gap-1">
                  <span className="text-content-base-default">{item.name}</span>
                  {'quantity' in item && (
                    <div className="whitespace-nowrap text-content-base-subdued">
                      x {String(item.quantity)}
                    </div>
                  )}
                </div>
                <span className="text-content-base-subdued">
                  {formatCurrency(item.price)}
                </span>
              </div>
            ))}
          </div>
        </PriceBreakdown>
        <form
          className="mt-6 flex flex-col gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <GraniteInput
            label="Order name"
            subtitle="Assign a unique name to this order to help you recognize it later"
            placeholder="E.g., HQ Primary Circuit, Store #2 Access"
            variant="blue"
            className="col-span-1"
            innerInputClassName="!w-full !box-border"
            {...register(`order_name`)}
            error={errors.order_name?.message}
          />
          <p className="mt-8 text-sm text-content-base-subdued">
            By clicking “Agree & submit,” you are: (1) confirming the above
            appointment; (2) agreeing that{' '}
            <a
              href="https://www.granitenet.com/wp-content/uploads/2025/06/Granite-Website-General-Terms-and-Conditions-of-Service-00074990xD85FF.pdf"
              target="_blank"
              rel="noreferrer"
              download
              className="cursor-pointer text-content-accent-default underline visited:text-content-accent-default"
            >
              Granite’s Terms and Conditions of Service
            </a>
            , including those applicable to{' '}
            <a
              href="https://www.granitenet.com/wp-content/uploads/2025/07/Access-Services-Additional-Terms-and-Conditions-00075220xD85FF.pdf"
              target="_blank"
              rel="noreferrer"
              download
              className="cursor-pointer text-content-accent-default underline visited:text-content-accent-default"
            >
              Access
            </a>{' '}
            and/or{' '}
            <a
              href="https://www.granitenet.com/wp-content/uploads/2025/07/Broadband-Services-Additional-Terms-and-Conditions-of-Service-00075143xD85FF.pdf"
              target="_blank"
              rel="noreferrer"
              download
              className="cursor-pointer text-content-accent-default underline visited:text-content-accent-default"
            >
              Broadband Services
            </a>
            , govern these Services (except to the extent Customer and Granite
            have separately agreed to any other terms governing Granite’s
            provision of these Services); and (3) representing and warranting
            that, as a representative of Customer, you are authorized to sign on
            Customer’s behalf and bind it to these terms.
          </p>
          <Checkbox
            label="I agree to Granite’s Terms & Conditions"
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
          <div className="mt-8 flex flex-col gap-5 md:flex-row">
            <GraniteButton size="large" variant="secondary" onClick={close}>
              Cancel
            </GraniteButton>
            <GraniteButton
              size="large"
              disabled={!isChecked || isSubmitting || isCheckoutLoading}
              type="submit"
            >
              Agree & submit{' '}
              {isSubmitting || isCheckoutLoading ? (
                <Loader width="w-5" height="h-5" />
              ) : null}
            </GraniteButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};
